/**
 * postMessage wire-contract for the hosted-mode meeting popup window.
 *
 * When NG Meet runs embedded in TeamHub (hosted / Module Federation mode) we
 * launch meetings in a separate popup window served from NG Meet's OWN origin.
 * That standalone page already loads Jitsi's `external_api.js` (from our
 * `index.html`) and keeps the call isolated from the host's DOM/CSS — which
 * the embedded remote cannot do, because TeamHub serves its own HTML without
 * our Jitsi script tag.
 *
 * The popup runs standalone NG Meet and therefore has no access to TeamHub's
 * auth bridge (`window.PKCEAuth`). Instead, the opener (the embedded remote,
 * which DOES have the host identity) hands the tokens to the popup over
 * `postMessage`. These constants and types are the shared contract used by
 * both sides.
 */

export const MEETING_WINDOW_MESSAGE_SOURCE = 'ngmeet-meeting-window' as const;

/** Query-string flag set on the popup URL so the popup knows it is a meeting window. */
export const MEETING_WINDOW_QUERY_FLAG = 'popup' as const;
/** Query-string param carrying the opener's origin so the popup can target it. */
export const MEETING_WINDOW_PARENT_ORIGIN_PARAM = 'parentOrigin' as const;

/** The identity bits the opener hands to the popup. */
export interface MeetingWindowAuthSnapshot {
  idToken: string | null;
  accessToken: string | null;
}

/**
 * Device selection bridged across the origin boundary as *labels*, not ids.
 * `MediaDeviceInfo.deviceId` is salted per origin, so the host-page ids never
 * match the popup-origin ids; labels are stable and are what the popup applies
 * to Jitsi by (see SM-1151). `null` means "no selection for this kind".
 */
export interface MeetingWindowDeviceLabels {
  cameraLabel: string | null;
  microphoneLabel: string | null;
  audioOutputLabel: string | null;
}

/** popup -> opener: "I'm loaded, send me the current identity". */
export interface MeetingWindowReadyMessage {
  source: typeof MEETING_WINDOW_MESSAGE_SOURCE;
  type: 'ready';
}

/** popup -> opener: "give me a fresh token" (correlated by `requestId`). */
export interface MeetingWindowRequestAuthMessage {
  source: typeof MEETING_WINDOW_MESSAGE_SOURCE;
  type: 'request-auth';
  requestId: string;
}

/**
 * opener -> popup: the current identity. `requestId` is the id from a
 * `request-auth` message, or `null` for the unsolicited push that answers
 * the initial `ready`.
 */
export interface MeetingWindowAuthMessage {
  source: typeof MEETING_WINDOW_MESSAGE_SOURCE;
  type: 'auth';
  requestId: string | null;
  auth: MeetingWindowAuthSnapshot;
}

/**
 * The host's Meet Settings, as far as the meeting needs them.
 *
 * `buildJitsiConfigOverwrite` reads `sangoma-meet-settings` from whatever origin
 * it runs on. In hosted mode that is the popup's own (empty) origin storage, so
 * every host toggle silently fell back to `jitsiDefaultSettings` — the shared
 * cause of SM-1187 (devices), SM-1188 (start muted / video off) and SM-1190
 * (pre-meeting screen). Bridging this payload before the meeting initializes is
 * what makes those settings take effect.
 */
export interface MeetingWindowSettingsPayload {
  /** Flat `settings.meetings` map, e.g. `prejoinConfig.enabled`, `disableShortcuts`. */
  meetings: Record<string, boolean | string | number>;
  /** Jitsi start-state toggles; they live in the host's `devices` section. */
  startWithAudioMuted: boolean;
  startWithVideoMuted: boolean;
  /** Device selection, bridged by label (ids are origin-salted). */
  deviceLabels: MeetingWindowDeviceLabels;
}

/**
 * opener -> popup: the host's Meet Settings. Sent once after the initial
 * `ready`/`auth` handshake; the popup holds meeting initialization until it
 * arrives so config-time settings (prejoin, start-muted) are honored.
 */
export interface MeetingWindowSettingsMessage {
  source: typeof MEETING_WINDOW_MESSAGE_SOURCE;
  type: 'settings';
  settings: MeetingWindowSettingsPayload;
}

/**
 * popup -> opener: the meeting's current device selection (by label), sent once
 * when the meeting window is closing so the host Settings panel can reflect the
 * user's final in-meeting choice.
 */
export interface MeetingWindowDeviceUpdateMessage {
  source: typeof MEETING_WINDOW_MESSAGE_SOURCE;
  type: 'device-update';
  devices: MeetingWindowDeviceLabels;
}

/**
 * opener -> popup: the user clicked a link for a different room while this
 * meeting is running. Ask them whether to leave it and join the new one.
 *
 * The prompt lives in the meeting window rather than in TeamHub because this is
 * where the meeting being left actually is, and because the opener may have no
 * Meet UI mounted at all — the link can be clicked from Chat (SM-1254).
 */
export interface MeetingWindowSwitchRequestMessage {
  source: typeof MEETING_WINDOW_MESSAGE_SOURCE;
  type: 'switch-request';
  /** Correlates the answer, so a superseded prompt's reply can be ignored. */
  requestId: string;
  /** The room the user clicked, shown in the prompt. */
  meetingId: string;
}

/**
 * popup -> opener: the user's answer to a `switch-request`. Only the opener can
 * act on it: it owns the window handle, and on desktop navigating from the
 * opener is what tells the shell this is a room switch rather than a hangup.
 */
export interface MeetingWindowSwitchResponseMessage {
  source: typeof MEETING_WINDOW_MESSAGE_SOURCE;
  type: 'switch-response';
  requestId: string;
  accepted: boolean;
}

export type MeetingWindowMessage =
  | MeetingWindowReadyMessage
  | MeetingWindowRequestAuthMessage
  | MeetingWindowAuthMessage
  | MeetingWindowSettingsMessage
  | MeetingWindowDeviceUpdateMessage
  | MeetingWindowSwitchRequestMessage
  | MeetingWindowSwitchResponseMessage;

/**
 * Narrow an arbitrary `MessageEvent.data` to a `MeetingWindowMessage`. Origin
 * and `event.source` checks are the caller's responsibility — this only
 * validates the payload shape.
 */
export const isMeetingWindowMessage = (data: unknown): data is MeetingWindowMessage => {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const candidate = data as { source?: unknown; type?: unknown };
  return candidate.source === MEETING_WINDOW_MESSAGE_SOURCE && typeof candidate.type === 'string';
};
